/* eslint-disable camelcase */
/*
 *
 *
 *
 * README:
 *
 * Our `yi-graphql` package currently does not support @yi/core imports.
 *
 * Until we can support those imports, any changes committed to modules within the
 * `yi-core/` folder should include changes to the corresponding module in @yi/core.
 *
 * This module's corresponding location is: `yi-graphql/src/yi-core/gaia.js`
 *
 *
 *
 */

import * as gql from './gql';
import { SUBSCRIPTION_GATEWAYS } from './constants';
import { BrooklynUserSubscription } from './types/brooklyn';

const { STRIPE, GAIA } = SUBSCRIPTION_GATEWAYS;
const { VALIDATION_PENDING, VALIDATION_FAILED } = gql.typesClient.MigrationStatus;
const { Digital, Premium } = gql.typesClient.MembershipTier;
const { MONTH, YEAR } = gql.typesClient.MembershipPlanInterval;

export const PLAN_SKUS = {
  BASIC_MONTHLY: 'YI MO BASIC',
  BASIC_MONTHLY_WITH_TRIAL: 'YI 1W MO BASIC',
  BASIC_ANNUAL: 'YI YR BASIC',
  BASIC_ANNUAL_WITH_TRIAL: 'YI 1W YR BASIC',
  PREMIUM_MONTHLY: 'YI MO PREMIUM',
  PREMIUM_ANNUAL: 'YI YR PREMIUM',
  WINBACK_BASIC_ANNUAL_129: 'YI YR BASIC WB1',
  // WINBACK_BASIC_ANNUAL_99: 'YI YR BASIC WB2',
  BASIC_3_MONTH_PROMO: 'YI 3MO MO BASIC',
  BASIC_3_MONTH_CANCEL_DEFLECT: 'YI 3MO MO CD',

  // Temporary solution treating Gaia's Comp plan as Basic tier access
  COMP_PLAN: 'GTV CM 00 1M',
} as const;

export type PlanSku = (typeof PLAN_SKUS)[keyof typeof PLAN_SKUS];

interface Plan {
  membershipTier: gql.typesClient.MembershipTier;
  membershipInterval: gql.typesClient.MembershipPlanInterval;
  planSku: PlanSku;
  hasTrial: boolean;
  planSkuWithoutTrial?: PlanSku;
}

const plans: Plan[] = [
  /* * * * * * * * * Monthly * * * * * * * * */
  {
    planSku: PLAN_SKUS.BASIC_MONTHLY,
    membershipTier: Digital,
    membershipInterval: MONTH,
    hasTrial: false,
  },
  {
    planSku: PLAN_SKUS.BASIC_MONTHLY_WITH_TRIAL,
    membershipTier: Digital,
    membershipInterval: MONTH,
    hasTrial: true,
    planSkuWithoutTrial: PLAN_SKUS.BASIC_MONTHLY,
  },

  /* * * * * * * * * Annual * * * * * * * * */
  {
    planSku: PLAN_SKUS.BASIC_ANNUAL,
    membershipTier: Digital,
    membershipInterval: YEAR,
    hasTrial: false,
  },
  {
    planSku: PLAN_SKUS.BASIC_ANNUAL_WITH_TRIAL,
    membershipTier: Digital,
    membershipInterval: YEAR,
    hasTrial: true,
    planSkuWithoutTrial: PLAN_SKUS.BASIC_ANNUAL,
  },

  /* * * * * * * * * Premium * * * * * * * * */
  {
    planSku: PLAN_SKUS.PREMIUM_MONTHLY,
    membershipTier: Premium,
    membershipInterval: MONTH,
    hasTrial: false,
  },
  {
    planSku: PLAN_SKUS.PREMIUM_ANNUAL,
    membershipTier: Premium,
    membershipInterval: YEAR,
    hasTrial: false,
  },

  /* * * * * * * * * Special Offers * * * * * * * * */
  {
    planSku: PLAN_SKUS.WINBACK_BASIC_ANNUAL_129,
    membershipTier: Digital,
    membershipInterval: YEAR,
    hasTrial: false,
  },
  // {
  //   planSku: PLAN_SKUS.WINBACK_BASIC_ANNUAL_99,
  //   membershipTier: Digital,
  //   membershipInterval: YEAR,
  // },
  {
    planSku: PLAN_SKUS.BASIC_3_MONTH_PROMO,
    membershipTier: Digital,
    membershipInterval: MONTH,
    hasTrial: false,
  },
  {
    planSku: PLAN_SKUS.BASIC_3_MONTH_CANCEL_DEFLECT,
    membershipTier: Digital,
    membershipInterval: MONTH,
    hasTrial: false,
  },

  /* * * * * * * * * Other * * * * * * * * */
  // Temporary solution treating Gaia's Comp plan as Basic tier access
  {
    planSku: PLAN_SKUS.COMP_PLAN,
    membershipTier: Digital,
    membershipInterval: MONTH,
    hasTrial: false,
  },
];

export const getMembershipPlanFromSku = (planSku: string) => {
  const plan = plans.find(p => p.planSku === planSku);
  return { tier: plan?.membershipTier || null, interval: plan?.membershipInterval || null };
};

export const getPlanMetadata = (planSku: string) => plans.find(p => p.planSku === planSku) || null;

export const WINBACK_PLAN_SKU = PLAN_SKUS.WINBACK_BASIC_ANNUAL_129;
export const BASIC_3_MONTH_PROMO_SKU = PLAN_SKUS.BASIC_3_MONTH_PROMO;
export const BASIC_3_MONTH_CANCEL_DEFLECT_SKU = PLAN_SKUS.BASIC_3_MONTH_CANCEL_DEFLECT;

// Client/server differences
//   - server-side `subscription` has raw DB values
//   - client-side `subscription` has GraphQL values
export const shouldLockAccount = (
  featureFlag: { enabled: boolean } | null,
  subscription: gql.typesClient.Query_CurrentUserSubscription['CurrentUserSubscription'],
) => {
  if (!featureFlag?.enabled || !subscription) return false;

  const { gateway_shortname, migration_status } = subscription;
  if (gateway_shortname === STRIPE) return true;
  if (gateway_shortname === GAIA && migration_status === VALIDATION_PENDING) return true;
  if (gateway_shortname === GAIA && migration_status === VALIDATION_FAILED) return true;

  return false;
};

/**
 * Utility created to handle paid through date timings in UTC, where
 * a date should not be considered in the past until the day has ended
 */
export const hasDatePassedUTC = (date: Date) => {
  const now = new Date();

  // Year has passed
  if (now.getUTCFullYear() > date.getUTCFullYear()) {
    return true;
  }

  // Year current or passed AND month passed
  if (now.getUTCFullYear() >= date.getUTCFullYear() && now.getUTCMonth() > date.getUTCMonth()) {
    return true;
  }

  // Year current or passed AND month current or passed, AND date has passed
  if (
    now.getUTCFullYear() >= date.getUTCFullYear() &&
    now.getUTCMonth() >= date.getUTCMonth() &&
    now.getUTCDate() > date.getUTCDate()
  ) {
    return true;
  }

  return false;
};

/**
 * Parse YYYY-MM-DD date string as UTC Date
 */
export const parseBrooklynDateString = (dateString: string) => {
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return new Date(0);
  return new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)));
};

const GAIA_SUB_STATUS = { ACTIVE: 'Active', CANCELLED: 'Cancelled' };
const yiSubStatusByGaiaSubStatus = {
  [GAIA_SUB_STATUS.ACTIVE]: 'active',
  [GAIA_SUB_STATUS.CANCELLED]: 'canceled',
};
export const transformGaiaSubStatus = (status: string) =>
  yiSubStatusByGaiaSubStatus[status] || null;

// In Gaia's system, cancelling a subscription immediately changes the status to cancelled.
// In YI's system, a cancelled subscription retains active status until its paid through date has passed,
// and a flag indicating cancellation at period end is set true.
export const getSubscriptionStatusFromGaiaSub = (
  gaiaSub: BrooklynUserSubscription,
  onError?: (e: Error) => void,
) => {
  try {
    if (gaiaSub.status === GAIA_SUB_STATUS.CANCELLED) {
      const ptd = parseBrooklynDateString(gaiaSub.paidThroughDate);
      return hasDatePassedUTC(ptd) ? 'canceled' : 'active';
    }
    const status = transformGaiaSubStatus(gaiaSub.status);
    if (!status) {
      throw new Error(
        `Unsupported Gaia subscription status: ${gaiaSub.status} (subscriptionNumber: ${gaiaSub.subscriptionNumber})`,
      );
    }
    return status;
  } catch (e) {
    if (onError && e instanceof Error) onError(e);
  }
  return 'canceled';
};

/*
 * Clients must pass a valid `x-client-attributes` header to Brooklyn, or the request may fail.
 * This header impacts what content the client is permitted to receive.
 * https://gaiatv.atlassian.net/wiki/spaces/ENG/pages/1187512671/Content+Policies+and+Filtering+Available+Content+By+Client
 */
export const CLIENT_ATTR_HEADER_KEY = 'x-client-attributes';
export const WEB_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/yi,app/web-yi',
};
export const IOS_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/apple,app/mobile-ios-yi',
};
export const TVOS_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/apple,app/tv-tvos-yi',
};
export const ANDROID_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/google-play,app/mobile-android-yi',
};
export const GOOGLETV_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/google-play,app/tv-android-yi',
};
export const FIRETV_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/amazon-fire,app/tv-amazon-fire-yi',
};
export const CRON_CLIENT_ATTR_HEADER = {
  [CLIENT_ATTR_HEADER_KEY]: 'app-provider/yi,app/server-yi',
};
